/**
 * Agenda de endereços do cliente no cardápio digital.
 *
 * Dois modos, mesma API:
 *  - Autenticado (consumidor): fonte da verdade no servidor (account/enderecos).
 *  - Convidado (sem login): agenda local no aparelho (localStorage), até MAX itens,
 *    cada endereço com um uuid gerado no cliente. A sincronização com o servidor
 *    acontece no pedido (o backend faz upsert por uuid). Não há listagem pública
 *    por whatsapp (privacidade / anti-enumeração).
 */
import Vue from 'vue';
import store from '@/store';

// 1 endereço por tipo (casa/apartamento/trabalho) → no máximo 3
export const MAX_ENDERECOS = 3;

const STORAGE_KEY = 'zapermenu-enderecos';
const LEGACY_KEY = 'zapermenu-userdata';
const ULTIMO_KEY = 'zapermenu-endereco-ultimo';

const http = () => Vue.prototype.$http;

export function genUuid() {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export function isAuthenticated() {
    return !!(store.state.usuario && store.state.usuario.id);
}

// ---------------------------------------------------------------- guest (local)

function readLocal() {
    let list = [];
    try {
        list = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
        list = [];
    }

    if (!Array.isArray(list)) {
        list = [];
    }

    // migração do formato antigo (endereço único em 'zapermenu-userdata')
    if (!list.length) {
        const legacy = migrateLegacy();
        if (legacy) {
            list = [legacy];
            writeLocal(list);
        }
    }

    return ordenar(garantirPadrao(list));
}

function writeLocal(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ENDERECOS)));
}

function migrateLegacy() {
    let data;
    try {
        data = JSON.parse(localStorage.getItem(LEGACY_KEY));
    } catch (e) {
        return null;
    }

    if (!data || !(data.rua || data.bairro || data.numero)) {
        return null;
    }

    return {
        uuid: genUuid(),
        padrao: true,
        descricao: null,
        rua: data.rua || null,
        numero: data.numero || null,
        bairro: data.bairro || null,
        municipio: data.municipio || null,
        referencia: data.complemento || null,
        cep: data.cep || null,
        uf: data.uf || null,
        tipo_local: data.tipoLocal || null,
        nome_edificio: data.nomeEdificio || null,
        numero_apartamento: data.numeroApartamento || null,
        valor_frete: null,
    };
}

function ordenar(list) {
    return [...list].sort((a, b) => (b.padrao ? 1 : 0) - (a.padrao ? 1 : 0));
}

// garante exatamente 1 padrão: mantém o primeiro marcado e zera os demais;
// se nenhum estiver marcado, promove o primeiro da lista.
function garantirPadrao(list) {
    let achou = false;
    list.forEach((e) => {
        if (e.padrao && !achou) {
            achou = true;
        } else if (e.padrao) {
            e.padrao = false;
        }
    });
    if (list.length && !achou) {
        list[0].padrao = true;
    }
    return list;
}

function isUuid(v) {
    return typeof v === 'string'
        && /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(v);
}

/**
 * Aplica a resposta do sync por uuid ao cache local: atualiza os campos dos endereços
 * cujo uuid voltou do servidor, remove os que o lojista excluiu (removed) e mantém os
 * desconhecidos (offline / de outro contexto). Reaplica o padrão único.
 */
function aplicarSync(local, servidor, removed) {
    const removidos = new Set(removed);
    const porUuid = new Map(servidor.map(e => [e.uuid, e]));

    const atualizados = local
        .filter(e => !removidos.has(e.uuid))
        .map(e => (porUuid.has(e.uuid) ? { ...e, ...porUuid.get(e.uuid) } : e));

    return garantirPadrao(atualizados);
}

// ---------------------------------------------------------------- API unificada

function normalizarTipoLocal(t) {
    const v = (t || '').toString().trim().toLowerCase();
    return ['casa', 'apartamento', 'trabalho'].includes(v) ? v : null;
}

/**
 * Upsert local por tipo_local (1 por tipo, máx 3). Atualiza o slot do tipo; se estava editando
 * outra linha (mudou o tipo p/ um já existente), remove a origem. somenteSeVazio: não sobrescreve
 * um slot já preenchido. Sem tipo → legado (por uuid / push respeitando o teto).
 */
function upsertLocalPorTipo(list, endereco, somenteSeVazio = false) {
    const tipo = normalizarTipoLocal(endereco.tipo_local);

    if (!tipo) {
        const i = endereco.uuid ? list.findIndex(e => e.uuid === endereco.uuid) : -1;
        if (i !== -1) {
            list[i] = { ...list[i], ...endereco };
            return list[i];
        }
        if (list.length < MAX_ENDERECOS) {
            const novo = { ...endereco, uuid: endereco.uuid || genUuid() };
            list.push(novo);
            return novo;
        }
        return list[0];
    }

    const alvo = list.find(e => normalizarTipoLocal(e.tipo_local) === tipo);
    const iOrigem = endereco.uuid ? list.findIndex(e => e.uuid === endereco.uuid) : -1;

    if (alvo) {
        if (somenteSeVazio) {
            return alvo;
        }
        Object.assign(alvo, endereco, { tipo_local: tipo, uuid: alvo.uuid });
        // moveu de outro slot p/ um já existente → remove a origem
        if (iOrigem !== -1 && list[iOrigem] !== alvo) {
            list.splice(iOrigem, 1);
        }
        return alvo;
    }

    if (iOrigem !== -1) {
        list[iOrigem] = { ...list[iOrigem], ...endereco, tipo_local: tipo };
        return list[iOrigem];
    }

    const novo = { ...endereco, tipo_local: tipo, uuid: endereco.uuid || genUuid() };
    list.push(novo);
    return novo;
}

export default {
    MAX_ENDERECOS,
    genUuid,
    isAuthenticated,

    /**
     * Ao logar/criar acesso: migra os endereços do convidado (localStorage) para a conta,
     * com dedup e máx no servidor. Limpa o cache local só em caso de sucesso (idempotente:
     * lê a chave crua, sem migrar o formato legado, p/ não re-migrar a cada boot).
     */
    async mergeLocalIntoAccount() {
        if (!isAuthenticated()) {
            return;
        }

        let local;
        try {
            local = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        } catch (e) {
            local = [];
        }

        if (!Array.isArray(local) || !local.length) {
            return;
        }

        try {
            const { data } = await http().post('account/enderecos/merge', { enderecos: local });
            if (data.type === 'success') {
                localStorage.removeItem(STORAGE_KEY); // a conta passa a ser a fonte da verdade
            }
        } catch (e) {
            // mantém o local; tenta de novo no próximo login
        }
    },

    // uuid do último endereço usado num pedido (p/ pré-seleção no checkout)
    setUltimoUuid(uuid) {
        if (uuid) {
            localStorage.setItem(ULTIMO_KEY, uuid);
        }
    },

    getUltimoUuid() {
        return localStorage.getItem(ULTIMO_KEY) || null;
    },

    async list() {
        if (isAuthenticated()) {
            const { data } = await http().get('account/enderecos');
            return data.type === 'success' ? data.data : [];
        }
        return readLocal();
    },

    /**
     * Convidado: sincroniza o cache local com o servidor POR UUID (não por WhatsApp).
     * Manda os uuids que já tem no localStorage e recebe a versão atualizada + os
     * removidos pelo lojista. Sem uuids → nada a sincronizar. Autenticado → o servidor
     * já é a fonte da verdade (list()).
     */
    async sincronizar() {
        if (isAuthenticated()) {
            return this.list();
        }

        const local = readLocal();
        const uuids = local.map(e => e.uuid).filter(isUuid);
        if (!uuids.length) {
            return local;
        }

        try {
            const { data } = await http().post('enderecos/sync', { uuids });
            if (data.type !== 'success') {
                return local;
            }
            const atualizados = aplicarSync(local, data.data || [], data.removed || []);
            writeLocal(atualizados);
            return ordenar(atualizados);
        } catch (e) {
            return local; // offline/erro: mantém o cache
        }
    },

    async create(endereco) {
        if (isAuthenticated()) {
            const { data } = await http().post('account/enderecos', endereco);
            if (data.type !== 'success') {
                throw new Error(data.msg || 'Não foi possível salvar o endereço');
            }
            return data.data;
        }

        const list = readLocal();
        const novo = upsertLocalPorTipo(list, endereco); // upsert por tipo (1 por tipo)
        if (endereco.padrao || list.length === 1) {
            list.forEach(e => (e.padrao = e === novo));
        }
        writeLocal(garantirPadrao(list));
        return novo;
    },

    async update(uuid, endereco) {
        if (isAuthenticated()) {
            const { data } = await http().put(`account/enderecos/${uuid}`, endereco);
            if (data.type !== 'success') {
                throw new Error(data.msg || 'Não foi possível atualizar o endereço');
            }
            return data.data;
        }

        const list = readLocal();
        // upsert por tipo (trocar o tipo p/ um já existente move p/ aquele slot)
        const salvo = upsertLocalPorTipo(list, { ...endereco, uuid });
        if (endereco.padrao) {
            list.forEach(e => (e.padrao = e === salvo));
        }
        writeLocal(garantirPadrao(list));
        return salvo;
    },

    async remove(uuid) {
        if (isAuthenticated()) {
            const { data } = await http().delete(`account/enderecos/${uuid}`);
            if (data.type !== 'success') {
                throw new Error(data.msg || 'Não foi possível remover o endereço');
            }
            return data.data;
        }

        let list = readLocal().filter(e => e.uuid !== uuid);
        writeLocal(garantirPadrao(list));
        return list;
    },

    async setPadrao(uuid) {
        if (isAuthenticated()) {
            const { data } = await http().post(`account/enderecos/${uuid}/padrao`);
            if (data.type !== 'success') {
                throw new Error(data.msg || 'Não foi possível definir o endereço padrão');
            }
            return data.data;
        }

        const list = readLocal().map(e => ({ ...e, padrao: e.uuid === uuid }));
        writeLocal(list);
        return ordenar(list);
    },

    /**
     * Guest: registra/atualiza no cache local o endereço usado num pedido.
     * (No autenticado, quem persiste é o próprio endpoint do pedido, no servidor.)
     */
    rememberLocal(endereco) {
        if (isAuthenticated() || !endereco) {
            return;
        }

        const list = readLocal();
        upsertLocalPorTipo(list, endereco); // upsert por tipo (1 por tipo)
        writeLocal(garantirPadrao(list));
    },
};
